import React from "react"
import PropTypes from "prop-types"
import { Alert, Button, Card, Flex } from "antd"
import { useTranslation } from "react-i18next"
import { Title } from "ui/Typography"
import { numberToCurrency } from "helpers/string"
import { useDevice } from "hooks/useDevice"
// The guest widget's cart describes a booking the same way; New Booking's selections have its shape.
import { describeBookingDate, describeSelectionGuests } from "pagesV2/BookingWidget/bookingDescription"
import { getExperienceCoverImage } from "pagesV2/BookingWidget/helpers"
import { getAddOnLines, getItemPrices, sumItemPrices } from "../helpers/priceLines"
import styles from "./BookingForm.module.css"

// "Waterproof Dry Bag x 2, Fishing Rod Holder x 1": the same add-ons the price lines count.
const describeAddOns = (experience, addOns, t) =>
  getAddOnLines(addOns, experience?.add_ons)
    .map((line) => t("booking.line_quantity", { label: line.title, count: line.count }))
    .join(", ")

// The price, with the price before the date's discount struck beside it (or above it, `isVertical`).
const StruckPrice = ({ price, before, isVertical }) => (
  <Flex vertical={isVertical} align={isVertical ? "flex-end" : "center"} gap={isVertical ? "var(--spacing-xxs)" : "var(--spacing-xs)"}>
    {before > price && <span className={styles.addedPriceStruck}>{numberToCurrency(before)}</span>}
    <span className={styles.addedPrice}>{numberToCurrency(price)}</span>
  </Flex>
)

StruckPrice.propTypes = {
  price: PropTypes.number.isRequired,
  before: PropTypes.number.isRequired,
  isVertical: PropTypes.bool
}

StruckPrice.defaultProps = {
  isVertical: false
}

// Image, details, then price with Edit / Remove at the side; on a phone the side drops under the
// details as one row. An experience the API refused (`error`) says why underneath.
const AddedExperience = ({ item, error, onEdit, onRemove }) => {
  const { t } = useTranslation()
  const { isMobile } = useDevice()
  const { experience, selections } = item
  const coverImage = getExperienceCoverImage(experience)
  const addOns = describeAddOns(experience, selections?.addOns, t)
  const { total, beforeAdjustments } = getItemPrices(selections)
  const row = (
    <Flex wrap={isMobile} gap="var(--spacing-s)" className={styles.addedItem}>
      <div className={styles.addedItemImage}>{coverImage && <img src={coverImage} alt={experience?.title} />}</div>
      <Flex vertical gap="var(--spacing-xs)" className={styles.addedItemDetails}>
        <span className={styles.addedItemTitle}>{experience?.title}</span>
        <Flex vertical gap="var(--spacing-xxs)">
          <span className={styles.addedItemText}>{describeBookingDate({ experience, selection: selections, t })}</span>
          <span className={styles.addedItemText}>{describeSelectionGuests({ experience, selection: selections, t })}</span>
          {addOns && <span className={styles.addedItemAddOns}>{t("booking.added_experiences.add_ons_line", { list: addOns })}</span>}
        </Flex>
      </Flex>
      <Flex
        vertical={!isMobile}
        align={isMobile ? "center" : "flex-end"}
        justify="space-between"
        gap={isMobile ? "var(--spacing-xs)" : undefined}
        className={styles.addedItemSide}
      >
        <StruckPrice price={total} before={beforeAdjustments} isVertical={!isMobile} />
        <Flex gap="var(--spacing-l)">
          <Button type="link" className={styles.addedItemEdit} onClick={() => onEdit(item.key)}>
            {t("booking.added_experiences.edit")}
          </Button>
          <Button type="link" danger className={styles.addedItemRemove} onClick={() => onRemove(item.key)}>
            {t("booking.added_experiences.remove")}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  )
  if (!error) return row
  return (
    <Flex vertical gap="var(--spacing-xs)">
      {row}
      <Alert type="error" showIcon message={error} />
    </Flex>
  )
}

AddedExperience.propTypes = {
  item: PropTypes.shape({
    key: PropTypes.number.isRequired,
    experience: PropTypes.object,
    selections: PropTypes.object
  }).isRequired,
  error: PropTypes.string,
  onEdit: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired
}

AddedExperience.defaultProps = {
  error: null
}

// New Booking's "Added Experiences" (redesign, Figma 5795:173067): each experience with its price and
// Edit / Remove, then the order's experiences, add-ons and total.
const AddedExperiencesCard = ({ items, refusedItem, onEdit, onRemove }) => {
  const { t } = useTranslation()
  const totals = sumItemPrices(items)
  return (
    <Card className={styles.cardContainer}>
      <Title className={styles.cardTitle} level={5}>
        {t("booking.added_experiences.title")}
      </Title>
      <Flex vertical gap="var(--spacing-xl)">
        <Flex vertical gap="var(--spacing-s)">
          {items.map((item) => (
            <AddedExperience
              key={item.key}
              item={item}
              error={refusedItem?.key === item.key ? refusedItem.message : null}
              onEdit={onEdit}
              onRemove={onRemove}
            />
          ))}
        </Flex>
        <div>
          <Flex justify="space-between" className={styles.addedTotalsLine}>
            <span>{t("booking.added_experiences.experiences")}</span>
            <span className={styles.summaryAmount}>{numberToCurrency(totals.total - totals.addOnsTotal)}</span>
          </Flex>
          {totals.addOnsTotal > 0 && (
            <Flex justify="space-between" className={styles.addedTotalsLine}>
              <span>{t("booking.add_ons.title")}</span>
              <span className={styles.summaryAmount}>{numberToCurrency(totals.addOnsTotal)}</span>
            </Flex>
          )}
          <Flex justify="space-between" align="center" className={styles.addedTotalsTotal}>
            <span>{t("booking.summary.total")}</span>
            <StruckPrice price={totals.total} before={totals.beforeAdjustments} />
          </Flex>
        </div>
      </Flex>
    </Card>
  )
}

AddedExperiencesCard.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  // The experience the API refused on Submit, and why ({ key, message }); null otherwise.
  refusedItem: PropTypes.shape({ key: PropTypes.number, message: PropTypes.string }),
  onEdit: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired
}

AddedExperiencesCard.defaultProps = {
  refusedItem: null
}

export default AddedExperiencesCard
